import { AxiosInstance } from 'axios'
import { createOptimizedAPI } from '../utils'
import { IExpertise } from '../../../types/initialExpertise.types'
import {
  IConceptExperienceStats,
  IConceptSearchResultItem,
  IOverviewConceptResponse,
  IOverviewVisibleConceptResponse,
  IStandardizedConceptAllLanguages
} from '../../../types/concept.types'
import { userGraphqlApiFactory } from '../accounts/user'
import { IConceptScore, IExpertSearchResult } from '../../../types/experts.types'
import { InterfaceLanguage } from '../../../types/language.types'

interface IExpertiseTrendResponse {
  concept_id: string;
  current_weight: number;
  past_weight: number;
  until_period: string;
}
interface IConceptExpertCount {
  count: number
}

interface IExpertisePoints {
  score: number
  global_user_id: string
  global_concept_id: string
}

export type ILikelihoodBucket = 'Very Unlikely' | 'Unlikely' | 'Likely' | 'Very Likely'

export interface IExpertiseDimensions {
  first_interaction: string
  last_interaction: string
  interactions_since: number
  high_impact_interactions_since: number
  medium_impact_interactions_since: number
  low_impact_interactions_since: number
}

export interface IExpertiseDimensionsResponse {
  dimensions: IExpertiseDimensions | null
  likelihood: ILikelihoodBucket
  probability?: number | null
}

interface IExpertSearchResponse {
  label_matches: IConceptSearchResultItem[],
  experts: {
    global_user_id: string;
    score: number;
    concept_scores: IConceptScore[];
    has_verified_expertise: boolean
  }[]
}

export enum ExpertiseFlag {
  Hidden = 'hidden',
  Highlighted = 'highlighted',
  Blocked = 'blocked',
  Verified = 'verified'
}

export function ulfShieldExpertsApiFactory(api: AxiosInstance) {
  const baseUrl = '/api/v2/learning/experts'

  /**
   * Extensions are the methods that require further configuration, in this case a user graphql client.
   * All other methods don't need this mandatorily.
   * The 'extend' function ensures that a graphql client is passed before it exposes methods that depend on it.
   */
  const extensions = {
    /**
     * Expert search: It is required to extend this API client in order to retrieve users from their id
     *
     * @param userGraphQlApi User GraphQL client from this library
     */
    extend(userGraphQlApi: ReturnType<typeof userGraphqlApiFactory>) {
      return createOptimizedAPI({
        ...methods,
        /**
         * Search for experts on concept ids or text query.
         * In order to show expert search results, make sure the respective realm setting is active.
         * You may not be allowed to show expert search results due to regulatory reasons.
         *
         * @param input Text query or list of concept ids
         * @param language Language for the search
         * @param limit maximum number of returned experts, defaults to 30
         * @returns User objects with their expertise
         */
        async searchExperts(input: string | string[], language: InterfaceLanguage, limit = 30): Promise<IExpertSearchResult> {
          const body = typeof input === 'string'
            ? { text_query: input, language }
            : { concept_ids: input, language }

          const { data } = await api.post<IExpertSearchResponse>(`${baseUrl}/search`, body)
          const experts = (
            await userGraphQlApi.getMultipleUsersById(
              data.experts.map((expert) => expert.global_user_id).slice(0, limit)
            )
          )
          return {
            ...data,
            experts: data.experts.map((expert) => ({
              ...expert,
              ...experts.find(({ global_user_id }) => global_user_id === expert.global_user_id)!
            })).filter((expert) => !!(expert.global_user_id && expert.firstname && expert.lastname && expert.image))
          }
        }
      })
    }
  }

  const methods = {
    getExpertCountForConcept(concept_id: string) {
      return api.get<IConceptExpertCount>(`${baseUrl}/count`, {
        params: {
          concept_id
        }
      })
    },
    setInitialUserExpertise(id: string, concepts: IExpertise[]) {
      return api.post<void>(`${baseUrl}/${id}/expertise/initial`, { concepts })
    },
    getExpertiseTrendForConcept(globalUserId: string, conceptId: string, beforeDays = 180) {
      return api.get<IExpertiseTrendResponse>(`${baseUrl}/${globalUserId}/expertise/trend`, {
        params: {
          concept_id: conceptId,
          before_days: beforeDays
        }
      })
    },
    getExpertisePoints(concept_id: string, userId: string) {
      return api.get<IExpertisePoints>(`${baseUrl}/${userId}/expertise/experience`, {
        params: {
          concept_id
        }
      })
    },
    getExpertiseDimensions(userId: string, concept_id: string, since_date?: string) {
      return api.get<IExpertiseDimensionsResponse>(
        `${baseUrl}/${userId}/expertise/experience/expertise-dimensions/${concept_id}`,
        {
          params: since_date ? { since_date } : {}
        }
      )
    },
    searchExperience(userId: string, query: string) {
      return api.get<any>(`${baseUrl}/${userId}/expertise/experience/search`, {
        params: {
          query
        }
      })
    },
    getVisibleExperience(userId: string, params: { language: string, offset: number, limit: number | undefined, sort?: string }) {
      return api.get<IOverviewVisibleConceptResponse>(`${baseUrl}/${userId}/expertise/experience`, {
        params
      })
    },
    getHiddenExperience(userId: string, language: string) {
      return api.get<IOverviewConceptResponse[]>(`${baseUrl}/${userId}/expertise/experience/hidden`, {
        params: {
          language
        }
      })
    },
    getBlockedExperience(userId: string, language: string) {
      return api.get<IOverviewConceptResponse[]>(`${baseUrl}/${userId}/expertise/experience/blocked`, {
        params: {
          language
        }
      })
    },
    getVerifiedExperience(userId: string, language: string) {
      return api.get<IOverviewConceptResponse[]>(`${baseUrl}/${userId}/expertise/experience/verified`, {
        params: {
          language
        }
      })
    },
    getHighlightedExperienceWithFallback(userId: string) {
      return api.get<IStandardizedConceptAllLanguages[]>(`${baseUrl}/${userId}/expertise/experience/highlighted`)
    },
    setExperienceFlag(userId: string, conceptId: string, type: ExpertiseFlag) {
      return api.put<void>(`${baseUrl}/${userId}/expertise/experience`, {
        agent_has_concept_type: type,
        concept_id: conceptId
      })
    },
    deleteExperienceFlag(userId: string, conceptId: string) {
      return api.delete(
        `${baseUrl}/${userId}/expertise/experience`,
        {
          params: {
            concept_id: conceptId
          }
        }
      )
    },
    addManualExpertise(userId: string, concepts: IExpertise[]) {
      return api.post<void>(`${baseUrl}/${userId}/expertise/manual`, { concepts })
    },
    addManualExpertiseByEmail(email: string, concepts: { concept_id: string; source: string }[]) {
      return api.post<void>(`${baseUrl}/email/expertise/manual`, { email, concepts })
    },
    getExperienceStats(concept_id: string, userId: string) {
      return api.get<IConceptExperienceStats>(`${baseUrl}/${userId}/expertise/experience/stats`, {
        params: {
          concept_id
        }
      })
    },
    getExperienceCount(userId: string) {
      return api.get<{concept_experience_count: number}>(`${baseUrl}/${userId}/expertise/experience/count`)
    },
    getExpertiseUpdate(userId: string, since_date: string) {
      return api.get<{new_expertise: string[]}>(`${baseUrl}/${userId}/expertise/experience/expertise-updates`, {
        params: {
          since_date
        }
      })
    }

  }
  return createOptimizedAPI({ ...methods, ...extensions })
}
